import type { SidebarControl } from './nav.ts';

/**
 * The sidebar folds into a narrow rail of icons and opens again (#188), on desktop only: under
 * 880px it is the top bar and the fold does not apply (the CSS scopes the rail to wider screens
 * and `wide` keeps the tooltip out). The choice is remembered in this browser.
 *
 * In the rail each control keeps its accessible name (the text stays for screen readers, only out
 * of sight) and shows it in one tooltip, on hover and on keyboard focus; Escape hides it.
 */

export const SIDEBAR_KEY = 'nubi.sidebar';

/** Browser storage, or null where the browser refuses it (some private windows). */
export type SidebarStore = Pick<Storage, 'getItem' | 'setItem'> | null;

/** Whether the person left the sidebar collapsed in this browser (open by default). */
export function rememberedCollapsed(store: SidebarStore): boolean {
  try {
    return store?.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

export function rememberCollapsed(store: SidebarStore, collapsed: boolean): void {
  try {
    store?.setItem(SIDEBAR_KEY, collapsed ? 'collapsed' : 'expanded');
  } catch {
    // Storage refused: the choice still holds for this visit.
  }
}

export const isCollapsed = (shell: HTMLElement) => shell.dataset.sidebar === 'collapsed';

/** Folds or opens the sidebar; the button says its state and what it does next. */
export function setCollapsed(shell: HTMLElement, toggle: HTMLElement, collapsed: boolean): void {
  shell.dataset.sidebar = collapsed ? 'collapsed' : 'expanded';
  toggle.setAttribute('aria-expanded', String(!collapsed));
  toggle.setAttribute('aria-label', collapsed ? 'Expandir menú' : 'Contraer menú');
}

/** The name a control of the sidebar says: its label, else its visible text (without counts). */
export function tipText(control: HTMLElement): string {
  const named =
    control.getAttribute('aria-label') ??
    control.querySelector('.nav-text, .nav-toggle-text, .label')?.textContent ??
    control.textContent;
  return (named ?? '').trim();
}

export type SidebarParts = {
  shell: HTMLElement;
  toggle: HTMLButtonElement;
  /** The sidebar's controls the tooltip names (the toggle and the menu). */
  nav: HTMLElement;
  tip: HTMLElement;
  storage: () => SidebarStore;
  /** Whether the window is wide enough for a sidebar (above the phone's 880px). */
  wide: () => boolean;
};

/**
 * Starts the sidebar as the person left it and wires its button and the rail's tooltip. Returns
 * what the menu needs to open the sidebar from a group's button in the rail.
 */
export function wireSidebar(parts: SidebarParts): SidebarControl {
  const { shell, toggle, tip, storage, wide } = parts;
  const sidebar = toggle.closest<HTMLElement>('.sidebar') ?? parts.nav;
  const railed = () => isCollapsed(shell) && wide();
  const hide = () => {
    tip.hidden = true;
  };
  const show = (control: HTMLElement) => {
    if (!railed()) return hide();
    const text = tipText(control);
    if (!text) return hide();
    tip.textContent = text;
    tip.hidden = false;
    const r = control.getBoundingClientRect();
    tip.style.setProperty('--tip-x', `${Math.round(sidebar.getBoundingClientRect().right)}px`);
    tip.style.setProperty('--tip-y', `${Math.round(r.top + r.height / 2)}px`);
  };
  /** The option of the screen on view, or its group's button when the group is closed. */
  const onView = () => {
    const at = parts.nav.querySelector<HTMLElement>('[aria-current="page"]');
    if (!at || at.getClientRects().length) return at;
    return at.closest('.nav-group')?.querySelector<HTMLElement>('.nav-toggle') ?? null;
  };
  const set = (collapsed: boolean) => {
    setCollapsed(shell, toggle, collapsed);
    rememberCollapsed(storage(), collapsed);
    // The rail (or the menu opened again) keeps the screen on view in sight: folding with several
    // groups open left it out of the rail (#242).
    showInMenu(onView());
    if (document.activeElement instanceof HTMLElement && sidebar.contains(document.activeElement))
      show(document.activeElement);
    else hide();
  };

  setCollapsed(shell, toggle, rememberedCollapsed(storage()));
  toggle.addEventListener('click', () => set(!isCollapsed(shell)));

  const control = (ev: Event) =>
    (ev.target as HTMLElement | null)?.closest?.<HTMLElement>('button');
  sidebar.addEventListener('focusin', (ev) => {
    const c = control(ev);
    if (c) show(c);
  });
  sidebar.addEventListener('focusout', hide);
  sidebar.addEventListener('pointerover', (ev) => {
    const c = control(ev);
    if (c) show(c);
    else hide();
  });
  sidebar.addEventListener('pointerleave', hide);
  sidebar.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') hide();
  });
  // The menu scrolls inside the sidebar: the tooltip follows the option the keyboard is on (the
  // menu scrolled to show it whole); one left behind by the pointer would point at nothing. The
  // fold button's stays: it does not move with the menu (folding scrolls the rail, #242).
  parts.nav.addEventListener(
    'scroll',
    () => {
      const at = document.activeElement;
      if (at instanceof HTMLElement && (parts.nav.contains(at) || at === toggle) && !tip.hidden)
        show(at);
      else hide();
    },
    { passive: true },
  );
  watchMore(parts.nav);

  return { collapsed: railed, expand: () => set(false) };
}

/*
 * The menu scrolls inside the sidebar when it does not fit (a short window, several groups open).
 * Its edge fades on the side where there is more to scroll to, and only there, so the last option
 * in sight never reads as the end of the menu (#190). An option the person reaches (the keyboard,
 * a click, the screen on view) is brought fully into view, clear of the faded edge.
 */

/** Where a scroll box has more to show: above, below, both, or nowhere (it all fits). */
export type More = 'above' | 'below' | 'both' | null;

export function moreIn(scroll: { top: number; height: number; client: number }): More {
  // A pixel of slack: zoom and fractional layouts leave the end a hair short of the height.
  const above = scroll.top > 1;
  const below = scroll.top + scroll.client < scroll.height - 1;
  return above && below ? 'both' : above ? 'above' : below ? 'below' : null;
}

type Span = { top: number; bottom: number };

/**
 * How far to scroll a box (its visible span) so an element's span is fully in it, `pad` away from
 * the edges (the faded band): 0 when it already is. An element taller than the room shows its top.
 * The browser clamps the result at the ends, where there is no fade.
 */
export function scrollToShow(view: Span, item: Span, pad: number): number {
  const room = view.bottom - view.top - 2 * pad;
  if (item.top < view.top + pad || item.bottom - item.top > room)
    return item.top - (view.top + pad);
  if (item.bottom > view.bottom - pad) return item.bottom - (view.bottom - pad);
  return 0;
}

/** Scrolls the menu that holds an element (an option, a group) just enough to show it whole. */
export function showInMenu(el: Element | null | undefined): void {
  const nav = el?.closest<HTMLElement>('.nav');
  if (!el || !nav || nav.scrollHeight <= nav.clientHeight) return;
  const pad = Number.parseFloat(getComputedStyle(nav).scrollPaddingTop) || 0;
  const delta = scrollToShow(nav.getBoundingClientRect(), el.getBoundingClientRect(), pad);
  if (delta !== 0) nav.scrollTop += delta;
}

/** Keeps the menu's `data-more` (what the CSS fades) in step with its scroll and its size. */
export function watchMore(nav: HTMLElement): () => void {
  const update = () => {
    const more = moreIn({ top: nav.scrollTop, height: nav.scrollHeight, client: nav.clientHeight });
    if (more) nav.dataset.more = more;
    else delete nav.dataset.more;
  };
  nav.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  // A group that opens or closes changes the menu's height; so does folding the sidebar.
  if (typeof ResizeObserver !== 'undefined') {
    const sizes = new ResizeObserver(update);
    sizes.observe(nav);
    for (const child of nav.children) sizes.observe(child);
  }
  update();
  return update;
}
